import { KeyRound } from "lucide-react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";

import { RequestError } from "../../lib/api";
import { enrollWithPasskey, invitationFor, passkeyCancelled, passkeysSupported } from "../../lib/passkeys";
import { useSession } from "../../lib/session";
import { Button, SectionMessage, Skeleton, TextField } from "../../ui";
import { useDocumentTitle } from "../../ui/shell";
import { AuthLayout } from "./AuthLayout";
import styles from "./auth.module.css";

type InvitationState = { status: "loading" } | { status: "valid"; email: string; name: string } | { status: "invalid" };

export function EnrollPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { adopt } = useSession();
  const [token] = useState(() => window.location.hash.replace(/^#/, ""));
  const [invitation, setInvitation] = useState<InvitationState>({ status: "loading" });
  const [deviceName, setDeviceName] = useState("");
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);

  useDocumentTitle(t("auth.enroll.documentTitle"));

  useEffect(() => {
    if (!token) {
      setInvitation({ status: "invalid" });
      return;
    }
    navigate("/enroll", { replace: true });
    invitationFor(token)
      .then((result) => setInvitation({ status: "valid", email: result.operator.email, name: result.operator.name }))
      .catch(() => setInvitation({ status: "invalid" }));
  }, [navigate, token]);

  const enroll = async () => {
    setBusy(true);
    setFailure(null);
    try {
      const info = await enrollWithPasskey(token, deviceName.trim());
      adopt(info);
      navigate("/", { replace: true });
    } catch (error) {
      if (passkeyCancelled(error)) setFailure(t("auth.enroll.cancelled"));
      else if (error instanceof RequestError && error.status === 409) setFailure(t("auth.enroll.duplicate"));
      else setFailure(t("auth.enroll.failed"));
    } finally {
      setBusy(false);
    }
  };

  if (invitation.status === "loading") {
    return (
      <AuthLayout title={t("auth.enroll.title")} subtitle={t("auth.enroll.checking")}>
        <Skeleton height="40px" />
      </AuthLayout>
    );
  }

  if (invitation.status === "invalid") {
    return (
      <AuthLayout title={t("auth.enroll.invalidTitle")} subtitle={t("auth.enroll.invalid")}>
        <Button variant="default" size="lg" fullWidth onClick={() => navigate("/sign-in", { replace: true })}>
          {t("auth.enroll.goToSignIn")}
        </Button>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout title={t("auth.enroll.title")} subtitle={t("auth.enroll.subtitle", { email: invitation.email })}>
      {!passkeysSupported() ? <SectionMessage tone="warning" title={t("auth.unsupportedTitle")}>{t("auth.unsupported")}</SectionMessage> : null}
      {failure ? <SectionMessage tone="danger" title={t("auth.enroll.failedTitle")}>{failure}</SectionMessage> : null}
      <form
        className={styles.form}
        onSubmit={(event) => {
          event.preventDefault();
          void enroll();
        }}
      >
        <TextField
          label={t("auth.enroll.deviceName")}
          controlSize="lg"
          value={deviceName}
          maxLength={80}
          placeholder={t("auth.enroll.devicePlaceholder")}
          onChange={(event) => setDeviceName(event.target.value)}
          disabled={busy}
        />
        <Button
          type="submit"
          variant="primary"
          size="lg"
          fullWidth
          loading={busy}
          disabled={!passkeysSupported()}
          iconBefore={<KeyRound size={16} strokeWidth={1.75} aria-hidden="true" />}
        >
          {t("auth.enroll.action")}
        </Button>
      </form>
    </AuthLayout>
  );
}
